import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { subscriberChatApi, type FriendlyBuildProgress, type FactoryBuildView, type SubStepView } from '../hooks/useSubscriberChat';


export interface BuildProgressStep {
  id: string;
  label: string;
  detail: string | null;
  // ocn-S730-P0: 'failed' only ever appears when the step feed is sourced from
  // real chainSteps data (a chain step can fail without the whole factory
  // build being terminal — the driver retries in place). The phases[]-derived
  // fallback feed never produces it.
  status: 'complete' | 'active' | 'failed';
}

// ocn-S588-P0: fixed, always-visible stage stepper (Option A). One entry per
// subscriber-facing stage — see FACTORY_BUILD_STAGE_DEFS for the ground-truthed
// mapping from the real factory_build_status DB enum (factory-status.ts).
export type BuildStageStatus = 'complete' | 'active' | 'pending' | 'failed';

export interface BuildStage {
  id: string;
  label: string;
  status: BuildStageStatus;
  // ocn-S795-P0: server-derived sub-step tree for the expandable drill-down
  // (apps/api/src/routes/factory-builds.ts's stages[].subSteps, matched by
  // key === id). Always an array — [] means "nothing granular yet" and the
  // disclosure renders a graceful working/no-detail row for it, never an error.
  subSteps: SubStepView[];
}

export type LiveBuildProgress = FriendlyBuildProgress & {
  steps: BuildProgressStep[];
  status: FactoryBuildView['status'];
  stages: BuildStage[];
  currentStageIndex: number;
  friendlyError: string | null;
  // ocn-S592-P0: counters derived from the SAME source as the visible
  // stepper (buildFactoryStages) so they can never contradict it — see
  // deriveBuildProgressCounters.
  stepsDone: number;
  displayPercent: number;
  // ocn-S730-P0: real per-chain-step data straight from the API view, passed
  // through so BuildProgressCard can render "Step N of M" off the actual
  // chain, not the 4-bucket stage floor. null for legacy_worker builds or
  // before the first step event lands — callers must fall back to `stages`.
  chainSteps: FactoryBuildView['chainSteps'];
  /** True after repeated GET failures; the card remains visible but never pretends it is current. */
  connectionLost?: boolean;
  lastUpdatedAt?: string | null;
  /** Server timestamp for the current activity; never initialized from a client clock. */
  activeStartedAt?: string | null;
};

// ocn-S588-P0 Part 1: fixed, always-visible stage stepper (Option A). Position
// is always obvious ("Stage N of M"); the S585 animated sub-step feed nests
// under whichever stage is currently active.
// ocn-S592-P0: header/footer counters now derive from the SAME
// buildFactoryStages() array the stepper renders (deriveBuildProgressCounters),
// so they can never show "0%"/"0 steps done" while the stepper shows a stage
// already Done. Motion (bar sheen, ring-pulse, per-step dots) is always-on
// while the build is running and pauses on any terminal status.
// ocn-S795-P0: friendly one-word status for a sub-step, used next to a
// `fire` sub-step's label (e.g. "backend · building") and inside the
// generic disclosure row.
export function friendlySubStepStatusWord(status: SubStepView['status']): string {
  switch (status) {
    case 'completed': return 'done';
    case 'failed': return 'needs attention';
    case 'in_progress': return 'building';
    case 'skipped': return 'skipped';
    default: return 'queued';
  }
}

// Chrome-reset only — layout (flex/justify-content/gap) still comes from the
// existing .subscriber-build-stage__row CSS class so the grid this sits
// inside (design-system.css: 22px indicator column + 1fr content column)
// is untouched.
export const buildStageRowButtonStyle: React.CSSProperties = {
  background: 'transparent', border: 'none', padding: 0, margin: 0, width: '100%',
  textAlign: 'left', cursor: 'pointer', font: 'inherit', color: 'inherit',
};

export const buildSubStepsListStyle: React.CSSProperties = {
  marginTop: 6, marginLeft: 22, display: 'flex', flexDirection: 'column', gap: 4,
};

// ocn-S829-P0: was CSS Grid (`gridTemplateColumns: '14px minmax(0, 1fr) auto
// auto'`) — a row with only ONE child (the reassurance line / graceful
// fallback rows below) auto-places into the FIRST 14px track, squeezing the
// text to one-word-per-line. Flex has no fixed tracks to fall into; the text
// span below carries its own flex:1/minWidth:0 so it fills the row while the
// indicator/metrics spans stay flexShrink:0 at their natural width.
export const buildSubStepRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'baseline', gap: 8, fontSize: 12.5,
};

// Shared by the reassurance line, the graceful-fallback rows, and each real
// sub-step's label — the growable text column of buildSubStepRowStyle.
export const buildSubStepTextStyle: React.CSSProperties = {
  flex: 1, minWidth: 0,
};

// The leading status glyph (✓ / ! / pulse-dot / ·) — fixed width, never
// shrinks the growable text column.
export const buildSubStepIndicatorStyle: React.CSSProperties = {
  flexShrink: 0, width: 14,
};

export function formatElapsed(startedAt: string | null | undefined, endedAt: string | null | undefined, now: number): string {
  if (!startedAt) return '—';
  const end = endedAt ? Date.parse(endedAt) : now;
  const seconds = Math.max(0, Math.floor((end - Date.parse(startedAt)) / 1000));
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  if (minutes === 0) return `${remainder}s`;
  return `${minutes}m ${String(remainder).padStart(2, '0')}s`;
}

// ocn-S829-P0: elapsed + credits-used as ONE right-aligned metrics string
// (previously two independently-positioned spans that could render mashed
// together, e.g. "0s metering…") — middot-separated, e.g. "1m 12s · 38
// credits"; while metering data is absent the credits side reads "—" with a
// tooltip explaining why, never a bare blank/undefined-looking gap.
export function formatSubStepMetrics(elapsedLabel: string, creditsUsed: number | null | undefined): { text: string; title?: string } {
  if (creditsUsed == null) {
    return { text: `${elapsedLabel} · —`, title: 'metering coming online' };
  }
  return { text: `${elapsedLabel} · ${creditsUsed.toLocaleString()} credits` };
}

export function formatSubStepCredits(credits: number): string {
  return Math.round(credits).toLocaleString();
}

/** Sum only reported per-step credits. Missing values must not become invented 0 totals. */
export function sumCredits(stages: BuildStage[]): number | null {
  const values = stages.flatMap((stage: BuildStage) => stage.subSteps
    .map((step: SubStepView) => step.creditsUsed ?? step.credits_used)
    .filter((credits: number | null | undefined): credits is number => credits != null));
  return values.length > 0 ? values.reduce((total: number, credits: number) => total + credits, 0) : null;
}

export function BuildProgressCard({
  progress,
  onStartNewBuild,
}: {
  progress: LiveBuildProgress;
  onStartNewBuild?: () => void;
}): React.ReactElement {
  const [expandedStages, setExpandedStages] = useState<Record<string, boolean>>({});
  const [cancelInFlight, setCancelInFlight] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const requestCancel = async () => {
    setCancelInFlight(true);
    setCancelError(null);
    try {
      await subscriberChatApi.cancelFactoryBuild(progress.runId);
    } catch (err) {
      setCancelError(err instanceof Error ? err.message : 'Unable to cancel this build. Please try again.');
    } finally {
      setCancelInFlight(false);
    }
  };
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (progress.status !== 'building' && progress.status !== 'deploying') return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [progress.status]);
  const totalStages = progress.stages.length;
  const isLive = progress.status === 'live';
  // ocn-S792-P0: 'cancelled' never actually reaches this card (the poller
  // clears build+progress entirely on cancel), but treat it as a terminal
  // failure shape here too for robustness / any future direct caller.
  const isTerminalFailure = progress.status === 'failed' || progress.status === 'cancelled';
  const isTerminal = isLive || isTerminalFailure;

  const connectionNotice = progress.connectionLost && !isTerminal ? (
    <div role="status" aria-live="polite" style={connectionLostStyle}>
      Connection lost — retrying…{progress.lastUpdatedAt ? ` Last updated ${formatStaleTime(progress.lastUpdatedAt)}.` : ''}
    </div>
  ) : progress.connectionLost && progress.lastUpdatedAt ? (
    <div role="status" style={connectionLostStyle}>Last updated {formatStaleTime(progress.lastUpdatedAt)}.</div>
  ) : null;
  // Heartbeat is derived from the server's activity timestamp. Terminal and
  // timestamp-less legacy projections never invent a client-local elapsed
  // clock (they show the stable initial marker only).
  const activeStage = progress.stages.find((s) => s.status === 'active');
  const activeSinceRef = useRef<Record<string, number>>({});
  // Stamp the clock during render (not just in an effect) so the very first
  // render of a newly-active stage already reads "· 0s" instead of waiting
  // for an effect pass (also makes this deterministically testable via
  // renderToStaticMarkup, which never runs effects).
  if (activeStage && !isTerminal && progress.activeStartedAt && !activeSinceRef.current[activeStage.id]) {
    activeSinceRef.current[activeStage.id] = Date.parse(progress.activeStartedAt);
  }
  const [, setHeartbeatTick] = useState(0);
  useEffect(() => {
    if (isTerminal || !activeStage) return;
    const interval = setInterval(() => setHeartbeatTick((t) => t + 1), 3000);
    return () => clearInterval(interval);
  }, [isTerminal, activeStage]);
  const activeElapsedSeconds = activeStage && !isTerminal
    ? (progress.activeStartedAt && Number.isFinite(activeSinceRef.current[activeStage.id])
      ? Math.max(0, Math.floor((Date.now() - activeSinceRef.current[activeStage.id]) / 1000))
      : 0)
    : null;
  const activeElapsedLabel = activeElapsedSeconds === null ? ''
    : activeElapsedSeconds < 60 ? ` · ${activeElapsedSeconds}s` : ` · ${Math.floor(activeElapsedSeconds / 60)}m`;
  const showStillWorkingReassurance = activeElapsedSeconds !== null && activeElapsedSeconds > 90;
  // ocn-S730-P0: once the chain has reported real stepIndex/totalSteps, lead
  // with "Step N of M" (the actual chain shape) instead of the coarse
  // 4-bucket "Stage N of M". Falls back automatically once live/terminal or
  // when no chain step data exists yet (legacy_worker, or nothing landed).
  // ocn-S792-P0: the terminal-failure exclusion was previously MISSING here
  // despite this comment already claiming it existed — a failed build with
  // real chain-step data (partial dispatch before failure) rendered a
  // perpetual, never-updating "Step N of M" instead of ever announcing failure.
  const chainSteps = progress.chainSteps;
  const hasRealSteps = !isLive
    && !isTerminalFailure
    && chainSteps != null
    && chainSteps.currentStepIndex != null
    && chainSteps.totalSteps != null
    && chainSteps.totalSteps > 0;
  const stageLabel = isLive
    ? 'Live'
    // ocn-S792-P0: a failed/cancelled build must never keep reading as an
    // in-progress "Stage N of M" / "Step N of M" — the header itself must
    // say so.
    : isTerminalFailure
      ? (progress.status === 'failed' ? 'Build failed' : 'Build cancelled')
      : hasRealSteps
        ? `Step ${chainSteps!.currentStepIndex} of ${chainSteps!.totalSteps}`
        : `Stage ${progress.currentStageIndex + 1} of ${totalStages}`;
  const hasActiveStage = progress.stages.some((stage: BuildStage) => stage.status === 'active');
  const realStepsDone = hasRealSteps
    // The chain feed is authoritative for chain units, but older events can
    // omit completion transitions. The visible stage stepper is the same
    // projection and is a lower-bound proof of completed work; never render a
    // contradictory lower count (the S592 invariant).
    ? Math.max(
      chainSteps!.steps.filter((s) => s.status === 'completed').length,
      progress.stepsDone,
    )
    : null;
  const stepsDoneLabel = hasRealSteps
    ? `${realStepsDone} of ${chainSteps!.totalSteps} steps done`
    : progress.stepsDone === 0 && hasActiveStage
      ? 'starting…'
      : `${progress.stepsDone} of ${totalStages} steps done`;
  const spentCredits = sumCredits(progress.stages);

  return (
    <section style={buildCardStyle} aria-label="Live build progress">
      {connectionNotice}
      {isTerminalFailure && (
        <div style={terminalRecoveryStyle}>
          <strong>{progress.status === 'failed' ? 'Build failed' : 'Build cancelled'}</strong>
          {progress.status === 'failed' && progress.friendlyError && <span>{progress.friendlyError}</span>}
          <button type="button" style={ghostButtonStyle} onClick={onStartNewBuild}>Start a new build</button>
        </div>
      )}
      <div style={buildProgressMetaStyle}>
        <strong>{stageLabel}</strong>
        <span style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {spentCredits != null && (
            <span style={{ fontFamily: 'var(--font-mono, monospace)' }}>
              Spent so far: {formatSubStepCredits(spentCredits)} credits
            </span>
          )}
          <span>{progress.displayPercent}% complete</span>
        </span>
      </div>
      <div
        className="subscriber-build-progress-track"
        aria-label={`${progress.displayPercent}% complete`}
      >
        <div className="subscriber-build-progress-fill" style={{ width: `${progress.displayPercent}%` }} />
        {!isTerminal && <div className="subscriber-build-progress-sheen" aria-hidden="true" />}
      </div>

      <div className="subscriber-build-stage-stepper" role="list" aria-label="Build stages">
        {progress.stages.map((stage: BuildStage, index: number) => {
          // ocn-S795-P0: default collapsed, except the currently-active stage
          // OR a failed stage (both auto-expand so live/error detail is visible
          // without a click — a failure the subscriber has to hunt for defeats
          // the point of surfacing it). Once the subscriber has manually
          // toggled a stage, that explicit choice wins.
          const isExpanded = expandedStages[stage.id] ?? (stage.status === 'active' || stage.status === 'failed');
          const substepsId = `build-stage-substeps-${stage.id}`;
          return (
            <div
              className={`subscriber-build-stage subscriber-build-stage--${stage.status}`}
              key={stage.id}
              role="listitem"
            >
              <span className="subscriber-build-stage__indicator" aria-hidden="true">
                {stage.status === 'complete' ? '✓'
                  : stage.status === 'failed' ? '!'
                  : stage.status === 'active'
                    ? <span className="subscriber-animated-dots" aria-hidden="true"><span /><span /><span /></span>
                    : index + 1}
              </span>
              <div className="subscriber-build-stage__content">
                <button
                  type="button"
                  className="subscriber-build-stage__row"
                  style={buildStageRowButtonStyle}
                  aria-expanded={isExpanded}
                  aria-controls={substepsId}
                  onClick={() => setExpandedStages((prev) => ({ ...prev, [stage.id]: !isExpanded }))}
                >
                  <span className="subscriber-build-stage__label">{stage.label}</span>
                  <span className="subscriber-build-stage__state">
                    {stage.status === 'complete' ? 'Done'
                      : stage.status === 'failed' ? 'Needs attention'
                      : stage.status === 'active' ? `In progress${activeElapsedLabel}` : 'Upcoming'}
                  </span>
                  {isExpanded ? <ChevronUp size={14} aria-hidden="true" /> : <ChevronDown size={14} aria-hidden="true" />}
                </button>

                {/* ocn-S795-P0: expandable drill-down into this stage's live
                    sub-steps. Renders a graceful fallback row (never an error,
                    never a dead expander) when nothing granular has landed yet. */}
                {isExpanded && (
                  <div
                    id={substepsId}
                    className="subscriber-build-stage__substeps"
                    role="list"
                    aria-live="polite"
                    aria-label={`${stage.label} details`}
                    style={buildSubStepsListStyle}
                  >
                    {stage.status === 'active' && showStillWorkingReassurance && (
                      <div role="listitem" style={buildSubStepRowStyle}>
                        <span style={{ ...dimStyle, ...buildSubStepTextStyle }}>Still working — some builds take a few minutes.</span>
                      </div>
                    )}
                    {stage.subSteps.length === 0 ? (
                      <div role="listitem" style={buildSubStepRowStyle}>
                        <span style={{ ...dimStyle, ...buildSubStepTextStyle }}>
                          {stage.status === 'active' ? 'working…' : 'No detailed steps for this phase yet.'}
                        </span>
                      </div>
                    ) : (
                      stage.subSteps.map((sub: SubStepView) => {
                        const metrics = formatSubStepMetrics(formatElapsed(sub.startedAt, sub.endedAt, now), sub.creditsUsed);
                        return (
                          <div key={sub.key} role="listitem" style={buildSubStepRowStyle}>
                            <span aria-hidden="true" style={buildSubStepIndicatorStyle}>
                              {sub.status === 'completed' ? '✓'
                                : sub.status === 'failed' ? '!'
                                : sub.status === 'skipped' ? '–'
                                : sub.status === 'in_progress'
                                  ? <span className="subscriber-build-step__pulse-dot" />
                                  : '·'}
                            </span>
                            <span style={buildSubStepTextStyle}>
                              {sub.label}
                              {sub.kind === 'fire' && ` · ${friendlySubStepStatusWord(sub.status)}`}
                            </span>
                            {sub.detail && <span title={sub.detail} style={{ ...dimStyle, fontSize: 12, flexShrink: 0 }}>{sub.detail}</span>}
                            <span
                              title={metrics.title}
                              style={{ ...dimStyle, fontFamily: 'var(--font-mono, monospace)', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                              {metrics.text}
                            </span>
                          </div>
                        );
                      })
                    )}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={buildProgressFooterStyle}>
        <span>{stepsDoneLabel}</span>
        <span>{progress.currentTask.statusMessage}</span>
      </div>

      {progress.friendlyError && (
        <div className="subscriber-build-stage__error" role="alert">{progress.friendlyError}</div>
      )}

      {progress.canCancel && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 10 }}>
          {cancelError && <div role="alert" style={{ color: '#f87171' }}>{cancelError}</div>}
          <button
            style={ghostButtonStyle}
            type="button"
            disabled={cancelInFlight}
            aria-busy={cancelInFlight}
            onClick={() => setConfirmCancel(true)}
          >{cancelInFlight ? 'Cancelling…' : 'Cancel build'}</button>
        </div>
      )}
      {confirmCancel && (
        <div role="dialog" aria-modal="true" aria-labelledby="cancel-build-title" style={terminalRecoveryStyle}>
          <strong id="cancel-build-title">Stop this build?</strong>
          <span>Your chat and project will stay safe. You can start a new build later.</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" style={ghostButtonStyle} onClick={() => setConfirmCancel(false)}>Keep building</button>
            <button type="button" style={ghostButtonStyle} disabled={cancelInFlight} onClick={() => { setConfirmCancel(false); void requestCancel(); }}>Stop build</button>
          </div>
        </div>
      )}
    </section>
  );
}

export function formatStaleTime(value: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - Date.parse(value)) / 1000));
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

export const connectionLostStyle: React.CSSProperties = {
  padding: '8px 10px', borderRadius: 6, background: 'rgba(245, 158, 11, .12)',
  color: '#fbbf24', fontSize: 13,
};
export const terminalRecoveryStyle: React.CSSProperties = {
  display: 'flex', flexDirection: 'column', gap: 8, padding: 12,
  borderRadius: 6, background: 'rgba(239, 68, 68, .12)', color: '#fecaca',
};

export const dimStyle: React.CSSProperties = {
  color: 'var(--text-dim, #9ca3af)',
  fontSize: 13,
};

export const buildProgressMetaStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', gap: 12, color: 'var(--text-dim, #9ca3af)',
  fontSize: 12, fontWeight: 600,
};

export const buildProgressFooterStyle: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', gap: 12, marginTop: 10,
  color: 'var(--text-dim, #9ca3af)', fontSize: 12,
};

// ocn-S592-P0: restyled to the approved mockup (Option A) — dark card,
// gradient progress bar, tri-state steps. Uses --surface2/--border tokens.
export const buildCardStyle: React.CSSProperties = {
  alignSelf: 'flex-start',
  width: '100%',
  padding: 16,
  borderRadius: 12,
  border: '1px solid var(--border, #2A2A45)',
  background: 'var(--surface2, #18182F)',
};

export const ghostButtonStyle: React.CSSProperties = {
  minHeight: 44,
  padding: '10px 14px',
  border: '1px solid var(--border, #374151)',
  borderRadius: 6,
  background: 'transparent',
  color: 'var(--text, #e5e7eb)',
  cursor: 'pointer',
  fontSize: 13,
};